'use client';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Language {
  name: string;
  nativeName: string;
  code: string;
}

const LANGUAGES: Record<string, Language> = {
  en: { name: 'English', nativeName: 'English', code: 'en' },
  ar: { name: 'Arabic', nativeName: 'العربية', code: 'ar' },
  so: { name: 'Somali', nativeName: 'Soomaali', code: 'so' },
  fr: { name: 'French', nativeName: 'Français', code: 'fr' },
  sw: { name: 'Swahili', nativeName: 'Kiswahili', code: 'sw' },
  ur: { name: 'Urdu', nativeName: 'اردو', code: 'ur' },
  id: { name: 'Indonesian', nativeName: 'Bahasa Indonesia', code: 'id' },
  ms: { name: 'Malay', nativeName: 'Bahasa Melayu', code: 'ms' },
  tr: { name: 'Turkish', nativeName: 'Türkçe', code: 'tr' },
  hi: { name: 'Hindi', nativeName: 'हिन्दी', code: 'hi' },
  bn: { name: 'Bengali', nativeName: 'বাংলা', code: 'bn' },
};

interface TranslationEntry {
  id: string;
  speaker: string;
  speakerInitials: string;
  transcript: string;
  translation: string;
  sourceLang: string;
  targetLang: string;
  timestamp: string;
}

interface TranslatorPanelProps {
  myName: string;
  myInitials: string;
  otherName: string;
  otherInitials: string;
  onClose: () => void;
}

export default function TranslatorPanel({
  myName,
  myInitials,
  otherName,
  otherInitials,
  onClose,
}: TranslatorPanelProps) {
  const [myLang, setMyLang] = useState('so');
  const [otherLang, setOtherLang] = useState('ar');
  const [activeSpeaker, setActiveSpeaker] = useState<'me' | 'other'>('me');

  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState<'transcribing' | 'translating' | 'speaking' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [history, setHistory] = useState<TranslationEntry[]>([]);
  const [currentEntry, setCurrentEntry] = useState<TranslationEntry | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const historyEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    historyEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const sourceLang = activeSpeaker === 'me' ? myLang : otherLang;
  const targetLang = activeSpeaker === 'me' ? otherLang : myLang;
  const speakerName = activeSpeaker === 'me' ? myName : otherName;
  const speakerInitials = activeSpeaker === 'me' ? myInitials : otherInitials;

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  }, []);

  const playTranslation = useCallback(async (text: string, lang: string) => {
    stopAudio();
    setProcessingStep('speaking');
    try {
      const res = await fetch('/api/rafeeq/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, language: lang }),
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      audioUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setProcessingStep(null);
      audio.onerror = () => setProcessingStep(null);
      await audio.play();
    } catch {
      setProcessingStep(null);
    }
  }, [stopAudio]);

  const startRecording = useCallback(async () => {
    setError(null);
    stopAudio();
    setProcessingStep(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' :'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setIsRecording(true);
    } catch {
      setError('Microphone access denied. Please allow microphone access.');
    }
  }, [stopAudio]);

  const stopRecordingAndTranslate = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;

    setIsRecording(false);
    setIsProcessing(true);
    setProcessingStep('transcribing');

    recorder.stop();
    recorder.stream.getTracks().forEach((t) => t.stop());

    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
    });

    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    audioChunksRef.current = [];

    try {
      const fd = new FormData();
      fd.append('audio', audioBlob, 'recording.webm');
      fd.append('sourceLang', sourceLang);
      fd.append('targetLang', targetLang);

      setProcessingStep('translating');
      const res = await fetch('/api/rafeeq/translate', { method: 'POST', body: fd });
      const data = await res.json();

      if (!res.ok || data.error) {
        setError(data.error ?? 'Translation failed. Please try again.');
        setIsProcessing(false);
        setProcessingStep(null);
        return;
      }

      const entry: TranslationEntry = {
        id: `tr-${Date.now()}`,
        speaker: speakerName,
        speakerInitials,
        transcript: data.transcript,
        translation: data.translation,
        sourceLang,
        targetLang,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };

      setCurrentEntry(entry);
      setHistory((prev) => [...prev, entry]);
      setIsProcessing(false);

      // Auto-play the translation
      await playTranslation(data.translation, targetLang);
    } catch {
      setError('Translation failed. Please try again.');
      setIsProcessing(false);
      setProcessingStep(null);
    }
  }, [sourceLang, targetLang, speakerName, speakerInitials, playTranslation]);

  const swapLanguages = () => {
    setMyLang(otherLang);
    setOtherLang(myLang);
  };

  const isRTL = (lang: string) => ['ar', 'ur'].includes(lang);

  return (
    <div className="flex flex-col h-full bg-[#0e1018] border-l border-white/5">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/15 flex items-center justify-center">
            <Icon name="LanguageIcon" size={15} className="text-primary" />
          </div>
          <div>
            <p className="text-white text-sm font-semibold leading-tight">Live Translator</p>
            <p className="text-white/30 text-[10px]">Rafeeq Translation</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-white/30 hover:text-white hover:bg-white/8 transition-colors"
        >
          <Icon name="XMarkIcon" size={15} />
        </button>
      </div>

      {/* Language Selector */}
      <div className="px-4 py-3 border-b border-white/5 flex-shrink-0">
        <div className="flex items-center gap-2">
          {/* My language */}
          <div className="flex-1">
            <p className="text-white/30 text-[10px] uppercase tracking-wider mb-1">{myName.split(' ')[0]}</p>
            <select
              value={myLang}
              onChange={(e) => setMyLang(e.target.value)}
              className="w-full bg-white/5 border border-white/8 rounded-lg px-2.5 py-2 text-white text-xs outline-none focus:border-primary/40 transition-colors"
            >
              {Object.values(LANGUAGES).map((l) => (
                <option key={l.code} value={l.code} className="bg-[#0e1018]">
                  {l.nativeName} ({l.name})
                </option>
              ))}
            </select>
          </div>

          {/* Swap */}
          <button
            onClick={swapLanguages}
            className="mt-4 p-2 rounded-lg bg-white/5 border border-white/8 text-white/40 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
            title="Swap languages"
          >
            <Icon name="ArrowsRightLeftIcon" size={14} />
          </button>

          {/* Other language */}
          <div className="flex-1">
            <p className="text-white/30 text-[10px] uppercase tracking-wider mb-1">{otherName.split(' ')[0]}</p>
            <select
              value={otherLang}
              onChange={(e) => setOtherLang(e.target.value)}
              className="w-full bg-white/5 border border-white/8 rounded-lg px-2.5 py-2 text-white text-xs outline-none focus:border-primary/40 transition-colors"
            >
              {Object.values(LANGUAGES).map((l) => (
                <option key={l.code} value={l.code} className="bg-[#0e1018]">
                  {l.nativeName} ({l.name})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Active speaker toggle */}
        <div className="flex mt-2.5 bg-white/5 rounded-lg p-0.5">
          <button
            onClick={() => setActiveSpeaker('me')}
            className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-all ${
              activeSpeaker === 'me' ?'bg-primary text-white shadow' :'text-white/40 hover:text-white/70'
            }`}
          >
            {myName.split(' ')[0]} speaking
          </button>
          <button
            onClick={() => setActiveSpeaker('other')}
            className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-all ${
              activeSpeaker === 'other' ?'bg-primary text-white shadow' :'text-white/40 hover:text-white/70'
            }`}
          >
            {otherName.split(' ')[0]} speaking
          </button>
        </div>
      </div>

      {/* Translation History */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0">
        {history.length === 0 && !isProcessing && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/15 flex items-center justify-center mb-3">
              <Icon name="LanguageIcon" size={24} className="text-primary/60" />
            </div>
            <p className="text-white/40 text-sm font-medium mb-1">Ready to translate</p>
            <p className="text-white/20 text-xs leading-relaxed max-w-[180px]">
              Select who is speaking, then hold the mic button to record
            </p>
          </div>
        )}

        {history.map((entry) => (
          <div key={entry.id} className="bg-white/4 border border-white/6 rounded-xl p-3 space-y-2">
            {/* Speaker + time */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-[9px] font-bold text-primary">
                  {entry.speakerInitials}
                </div>
                <span className="text-white/50 text-xs">{entry.speaker.split(' ')[0]}</span>
                <span className="text-white/20 text-[10px]">
                  {LANGUAGES[entry.sourceLang]?.nativeName ?? entry.sourceLang}
                  {' → '}
                  {LANGUAGES[entry.targetLang]?.nativeName ?? entry.targetLang}
                </span>
              </div>
              <span className="text-white/20 text-[10px]">{entry.timestamp}</span>
            </div>

            {/* Original */}
            <div className="bg-white/3 rounded-lg px-2.5 py-2">
              <p className="text-white/30 text-[10px] uppercase tracking-wider mb-1">Original</p>
              <p
                className="text-white/70 text-xs leading-relaxed"
                dir={isRTL(entry.sourceLang) ? 'rtl' : 'ltr'}
              >
                {entry.transcript}
              </p>
            </div>

            {/* Translation */}
            <div className="bg-primary/8 border border-primary/15 rounded-lg px-2.5 py-2">
              <p className="text-primary/60 text-[10px] uppercase tracking-wider mb-1">Translation</p>
              <p
                className="text-white text-xs leading-relaxed font-medium"
                dir={isRTL(entry.targetLang) ? 'rtl' : 'ltr'}
              >
                {entry.translation}
              </p>
            </div>

            {/* Replay button */}
            <button
              onClick={() => playTranslation(entry.translation, entry.targetLang)}
              disabled={processingStep === 'speaking'}
              className="flex items-center gap-1.5 text-[10px] text-white/30 hover:text-primary transition-colors disabled:opacity-40"
            >
              <Icon name="SpeakerWaveIcon" size={11} />
              Replay audio
            </button>
          </div>
        ))}

        {/* Processing state */}
        {isProcessing && (
          <div className="bg-white/4 border border-white/6 rounded-xl p-4 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
              <div className="w-4 h-4 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
            </div>
            <div>
              <p className="text-white text-xs font-medium">
                {processingStep === 'transcribing' && 'Transcribing speech…'}
                {processingStep === 'translating' && 'Translating…'}
              </p>
              <p className="text-white/30 text-[10px]">
                {LANGUAGES[sourceLang]?.name} → {LANGUAGES[targetLang]?.name}
              </p>
            </div>
          </div>
        )}

        {/* Speaking state */}
        {processingStep === 'speaking' && (
          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
            <Icon name="SpeakerWaveIcon" size={13} className="text-emerald-400 flex-shrink-0" />
            <p className="text-emerald-300 text-xs">Playing translation in {LANGUAGES[targetLang]?.name}…</p>
            <button
              onClick={stopAudio}
              className="ml-auto text-white/30 hover:text-white transition-colors"
            >
              <Icon name="StopIcon" size={12} />
            </button>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 px-3 py-2.5 bg-red-500/10 border border-red-500/20 rounded-xl">
            <Icon name="ExclamationCircleIcon" size={13} className="text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-300 text-xs leading-relaxed">{error}</p>
          </div>
        )}

        <div ref={historyEndRef} />
      </div>

      {/* Mic Control */}
      <div className="px-4 py-4 border-t border-white/5 flex-shrink-0">
        <div className="flex flex-col items-center gap-2">
          {/* Direction indicator */}
          <p className="text-white/30 text-[10px] text-center">
            <span className="text-white/50">{LANGUAGES[sourceLang]?.nativeName}</span>
            {' → '}
            <span className="text-white/50">{LANGUAGES[targetLang]?.nativeName}</span>
          </p>

          {/* Mic button */}
          <button
            onMouseDown={startRecording}
            onMouseUp={stopRecordingAndTranslate}
            onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
            onTouchEnd={(e) => { e.preventDefault(); stopRecordingAndTranslate(); }}
            disabled={isProcessing}
            className={`relative w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200 select-none ${
              isRecording
                ? 'bg-red-500 border-2 border-red-400 scale-110 shadow-lg shadow-red-500/40'
                : isProcessing
                ? 'bg-white/8 border border-white/10 opacity-50 cursor-not-allowed' :'bg-primary border-2 border-primary/60 hover:scale-105 active:scale-95 shadow-lg shadow-primary/30'
            }`}
          >
            {isRecording && (
              <span className="absolute inset-0 rounded-full border-2 border-red-400 animate-ping opacity-60" />
            )}
            <Icon
              name={isRecording ? 'StopIcon' : 'MicrophoneIcon'}
              size={26}
              className="text-white"
            />
          </button>

          <p className="text-white/25 text-[10px] text-center">
            {isRecording
              ? 'Release to translate'
              : isProcessing
              ? 'Processing…' :'Hold to speak'}
          </p>
        </div>
      </div>
    </div>
  );
}
